import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';
import { chartColors } from '../../helpers/chartColors';
import NoChartData from './NoChartData';
import SimpleLinearRegression from 'ml-regression-simple-linear';
import { Typography } from '@mui/material';

export const RTFOCommandCenterStageProgressChart = ({ gridView = true }) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const totalStagesPlanned = (Object.keys(currentRTFO?.perfSchedule?.wells)?.map(w => Object.keys(currentRTFO?.perfSchedule?.wells?.[w]?.stages)?.slice(-1)?.[0])?.flat(Infinity))?.reduce((tot, curr) => +curr + tot, 0);

  const [expectedCompletion, setExpectedCompletion] = useState(null);

  const data = useMemo(() => {
    if (!currentRTFO?.stageData) return [];

    return getChartData();

    function getChartData() {
      try {
        const startTimestamp = new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.[0]]?.stageStartTimestamp)?.getTime();
        const endTimestamp = new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageEndTimestamp)?.getTime();

        const numDays = Math.ceil(Math.max(1, (endTimestamp - startTimestamp) / 1000 / 60 / 60 / 24));


        const traces = [];
        const X = [];
        const YDailyStages = [];
        const YCumulativeStages = [];

        for (let i = 0; i < numDays; i++) {
          const date = new Date(startTimestamp + i * 24 * 60 * 60 * 1000);
          const day = date.getDate();
          const month = date.getMonth();

          const dailyStageNames = currentRTFO?.stages?.filter(
            s =>
              new Date(currentRTFO?.stageData?.[s]?.stageStartTimestamp)?.getMonth() === month &&
              new Date(currentRTFO?.stageData?.[s]?.stageStartTimestamp)?.getDate() === day
          );

          // let totalPumpingTime = 0;

          // for (const stageName of dailyStageNames) {
          //   const stage = currentRTFO?.stageData?.[stageName];

          //   totalPumpingTime += stage?.metrics?.stageTime;
          // }

          YDailyStages.push(dailyStageNames?.length);
          YCumulativeStages.push(dailyStageNames?.length + (i === 0 ? 0 : YCumulativeStages[i-1]));
          X.push(date);
        }

        // daily stages trace
        traces.push({
          x: X,
          y: YDailyStages,
          type: 'bar',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Daily Stages',
          marker: {
            color: chartColors[0]
          }
        });
        // cumulative stages trace
        traces.push({
          x: X,
          y: YCumulativeStages,
          type: 'bar',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Cumulative Stages',
          marker: {
            color: chartColors[2]
          }
        });


        // Extrapolate completion date
        const regression = new SimpleLinearRegression(X.map((_,i) => i + 1), YCumulativeStages);


        const XExtrapolation = [];
        const YExtrapolation = [];

        let cumulativeStages = 0;
        let day = 1;

        while (cumulativeStages <= totalStagesPlanned && day < 120) {
          cumulativeStages = regression.predict(day);
          XExtrapolation.push(new Date(startTimestamp + (day - 1) * 24 * 60 * 60 * 1000));
          YExtrapolation.push(cumulativeStages);
          day++;
        };

        setExpectedCompletion(() => new Date(startTimestamp + (day - 2) * 24 * 60 * 60 * 1000)?.getTime());

        // cumulative stages extrapolation trace
        traces.push({
          x: XExtrapolation,
          y: YExtrapolation,
          mode: 'lines',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Cumulative Stages Extrapolation',
          line: {
            color: chartColors[2],
            width: 3,
            dash: 'dot'
          }
        });

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      }
    }
  }, [currentRTFO?.stageData, currentRTFO?.stages, totalStagesPlanned]);

  const layout = useMemo(() => {
    return {
      barmode: 'group',
      yaxis: {
        // Main y axis
        linecolor: darkMode ? 'lightgrey' : chartColors[0],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : chartColors[0],
        title: {
          text: 'Number of Stages',
          font: {
            size: 12
          }
        },
        // autorange: true,
        side: 'left',
        uirevision: 'true',
        rangemode: 'nonnegative'
      },
      xaxis: {
        // Main x axis
        autorange: true,
        rangemode: 'nonnegative',
        title: {
          text: 'Date',
          font: {
            size: 12
          }
        },
        tickfont: {
          size: 10
        },
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        type: 'date'
      },
      autosize: true,
      showlegend: true,
      legend: {
        orientation: 'h',
        yanchor: 'top',
        font: {
          color: darkMode ? 'lightgrey' : 'grey',
          size: 10
        },
        y: gridView ? -0.25 : -0.1
      },
      shapes: [
        totalStagesPlanned && expectedCompletion && { // horizontal line
          type: 'line',
          xref: 'x',
          yref: 'y',
          x0: new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.[0]]?.stageStartTimestamp)?.getTime(),
          y0: totalStagesPlanned,
          x1: expectedCompletion,
          y1: totalStagesPlanned,
          line: {
            color: 'grey',
            width: 2,
            dash: 'dot'
          }
        },
        totalStagesPlanned && expectedCompletion && { // vertical line
          type: 'line',
          xref: 'x',
          yref: 'y',
          x0: expectedCompletion,
          y0: 0,
          x1: expectedCompletion,
          y1: totalStagesPlanned,
          line: {
            color: 'grey',
            width: 2,
            dash: 'dot'
          }
        }
      ],
      annotations: [
        {
          text: 'Stage Progression Report',
          font: {
            size: 14,
            color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: gridView ? 1.1 : 1.06, //position in y domain
          xref: 'paper',
          yref: 'paper'
        },
        {
          text: `Pad: ${currentRTFO?.name}`,
          font: {
            size: 10,
            color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: gridView ? 1.05 : 1.035, //position in y domain
          xref: 'paper',
          yref: 'paper'
        }
      ],
      uirevision: 'true',
      margin: { t: 40, l: 60, r: 50, b: 0 },
      images: [
        {
          x: gridView ? 0.05 : 0.035,
          y: gridView ? 1.02 : 1.01,
          sizex: gridView ? 0.1 : 0.05,
          sizey: 0.1,
          source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: 'page',
          yref: 'y1 domain',
          xanchor: 'right',
          yanchor: 'bottom',
          opacity: 0.75
        }
      ],
      plot_bgcolor: 'transparent',
      paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
    }
  }, [currentRTFO?.name, currentRTFO?.stageData, currentRTFO?.stages, darkMode, expectedCompletion, gridView, theme?.palette?.elevationOne, totalStagesPlanned]);

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Stage progress-${new Date().toISOString().split('T')[0]}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {data?.length > 0 ? (
        <div>
          <Typography variant="caption" color="primary" style={{position: 'absolute', top: 20, right: 20, zIndex: 666}}>Expected Completion: {new Date(expectedCompletion)?.toLocaleDateString()}</Typography>
          <Plot data={data} layout={layout} config={config} useResizeHandler={true} style={{ width: '100%', height: '100%' }} />
        </div>
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <NoChartData />
        </div>
      )}
    </div>
  );
};
